'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import {
  ChevronUp,
  Settings,
  LogOut,
  Moon,
  Sun,
  Monitor,
  UserCircle,
  LifeBuoy,
  Download,
  Shield,
} from 'lucide-react';
import { useAuth } from '@/components/auth-provider';
import { useTheme } from '@/components/theme-provider';
import { useTranslation } from '@/components/i18n-provider';
import { Avatar } from '@/components/avatar';
import { haptic } from '@/lib/haptic';
import { displayPlanName } from '@/lib/plans/display';
import { useNotifications } from '@/hooks/useNotifications';
import { useAnalytics } from '@/hooks/use-analytics';
import { Tip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PopoverPortal, useAnchorRect } from '@/components/ui/popover-portal';

export type UserMenuUser = {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  accountType: 'human' | 'agent' | 'guest';
  role?: 'user' | 'moderator' | 'admin';
  reputationScore: string;
};

export type UserMenuItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  href?: string;
  ariaLabel?: string;
  trailing?: React.ReactNode;
};

export type UserMenuSection = {
  id: string;
  /** Optional small label shown above the section. */
  title?: string;
  items: UserMenuItem[];
};

export type UserMenuDropdownProps = {
  user: UserMenuUser;
  /** Where the dropdown panel opens relative to the trigger. */
  direction?: 'up' | 'down';
  /** Width of the dropdown panel in pixels. */
  width?: number;
  /** Sections inserted just above the Settings/Help section. */
  extraSections?: UserMenuSection[];
  /**
   * Arbitrary JSX appended to the menu body, after `extraSections` and
   * before the Settings/Help block. Use this when a caller needs richer
   * UI than `UserMenuItem[]` can express (e.g. an inline list with
   * rename/delete affordances).
   */
  customSlot?: React.ReactNode;
  /** Custom trigger renderer. Defaults to the avatar+name+chevron pill. */
  trigger?: (args: { open: boolean; toggle: () => void; user: UserMenuUser }) => React.ReactNode;
  /**
   * Render the dropdown body via React Portal at `document.body` with
   * `position: fixed` so it escapes any positioned/z-indexed ancestor
   * stacking context (e.g. a `z-30` workspace chrome row). The popover
   * sits at `z-[200]`, which puts it above workspace edge panels at
   * `z-[90]`.
   */
  portal?: boolean;
  /** Fires once when the menu transitions from closed → open. */
  onOpen?: () => void;
  /**
   * Optional navigation hook. When provided, in-app destinations
   * (Wallet, Analytics, Settings, Admin, the header's Manage/Upgrade
   * link) call this instead of triggering a full page navigation —
   * the workspace uses it to open those routes as full-screen
   * dialogs without losing canvas state. External links (Docs) and
   * the extraSections caller items are unaffected.
   */
  openHref?: (href: string) => void;
  className?: string;
};

export function UserMenuDropdown({
  user,
  direction = 'up',
  width = 260,
  extraSections = [],
  customSlot,
  trigger,
  portal = false,
  onOpen,
  openHref,
  className,
}: UserMenuDropdownProps) {
  const { logout } = useAuth();
  const { trackNavClick } = useAnalytics();
  const { theme, setTheme } = useTheme();
  const { t, locale } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [planName, setPlanName] = useState<string | null>(null);
  // Trigger wrapper — the click that toggles the menu must be considered
  // "inside" so the outside-handler doesn't immediately re-close it.
  const menuRef = useRef<HTMLDivElement>(null);
  // Portal body ref — when rendered into document.body, the menu DOM is
  // outside `menuRef`. We register this ref on the portaled root so the
  // outside-click handler can recognise clicks inside the floating body
  // as "inside".
  const portalBodyRef = useRef<HTMLDivElement>(null);
  const { unreadCount: notifUnread } = useNotifications(true);

  useEffect(() => {
    if (!isOpen) return;
    function handleOutside(event: PointerEvent) {
      const target = event.target as Node;
      const insideTrigger = menuRef.current?.contains(target);
      const insidePortal = portalBodyRef.current?.contains(target);
      if (!insideTrigger && !insidePortal) {
        setIsOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    // Capture phase so panels / overlays that stop propagation can't
    // prevent the menu from closing on outside taps.
    document.addEventListener('pointerdown', handleOutside, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handleOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !planName) {
      fetch('/api/balance')
        .then((r) => r.json())
        .then((data) => setPlanName(displayPlanName(data.planName)))
        .catch(() => {});
    }
  }, [isOpen, planName]);

  const close = () => setIsOpen(false);
  const toggle = () => {
    haptic('light');
    // Read previous state synchronously rather than via the updater so
    // the `onOpen` callback fires from an event handler, never inside
    // React's render phase. (Updaters can run during render, so calling
    // `setState` on a parent from inside one trips the "Cannot update a
    // component while rendering another component" warning.)
    const next = !isOpen;
    setIsOpen(next);
    if (next) onOpen?.();
  };

  // Anchor measurement for portal mode — keeps the popover stuck to
  // the trigger's viewport position even as the user resizes / scrolls.
  const triggerWrapperRef = useRef<HTMLDivElement>(null);
  const anchorRect = useAnchorRect(portal && isOpen, triggerWrapperRef);

  const displayName = user.displayName || user.username;
  // Guests get a simplified menu: no economy hub (Account) / Install / Settings,
  // and the plan row is replaced by a prominent "sign up to keep your work" CTA.
  const isGuest = user.accountType === 'guest';
  const isStaff = user.role === 'admin' || user.role === 'moderator';
  const goSignup = () => {
    close();
    if (openHref) openHref('/signup');
    else window.location.assign('/login?signup=1');
  };

  const themeOptions: { value: 'light' | 'dark' | 'system'; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: t('settings.light'), icon: Sun },
    { value: 'dark', label: t('settings.dark'), icon: Moon },
    { value: 'system', label: t('settings.system'), icon: Monitor },
  ];

  const defaultTrigger = (
    <button
      onClick={toggle}
      className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-accent transition-colors w-full"
    >
      <div className="relative shrink-0 h-8 w-8">
        <Avatar
          seed={user.username}
          type={user.accountType === 'agent' ? 'agent' : 'human'}
          src={user.avatarUrl}
          className="h-8 w-8"
        />
        {notifUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-background z-10">
            {notifUnread > 99 ? '99+' : notifUnread}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="text-sm font-medium truncate">{displayName}</div>
        <div className="text-[11px] text-muted-foreground truncate">@{user.username}</div>
      </div>
      <ChevronUp
        className={cn(
          'h-4 w-4 text-muted-foreground transition-transform shrink-0',
          isOpen && (direction === 'up' ? 'rotate-180' : 'rotate-0'),
          !isOpen && direction === 'down' && 'rotate-180',
        )}
      />
    </button>
  );

  const renderItem = (item: UserMenuItem) => {
    const inner = (
      <>
        <span className="h-4 w-4 flex items-center justify-center text-muted-foreground">
          {item.icon}
        </span>
        <span>{item.label}</span>
        {item.trailing && <span className="ml-auto">{item.trailing}</span>}
      </>
    );
    const cls =
      'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm hover:bg-accent transition-colors';
    if (item.href) {
      return (
        <Link
          key={item.id}
          href={item.href}
          aria-label={item.ariaLabel}
          onClick={() => {
            close();
            item.onClick?.();
          }}
          className={cls}
        >
          {inner}
        </Link>
      );
    }
    return (
      <button
        key={item.id}
        type="button"
        aria-label={item.ariaLabel}
        onClick={() => {
          close();
          item.onClick?.();
        }}
        className={cn(cls, 'w-full text-left')}
      >
        {inner}
      </button>
    );
  };

  // Body content extracted so it can render either inline (default)
  // or via Portal at document.body without duplicating markup. The wrapper
  // (border + bg + radius + width) is added by the caller-specific render
  // path below.
  const bodyContent = (
    <>
          {/* User header: avatar above name */}
          <div className="p-4 border-b border-border text-center">
            <Avatar
              seed={user.username}
              type={user.accountType === 'agent' ? 'agent' : 'human'}
              src={user.avatarUrl}
              className="h-14 w-14 mb-2 mx-auto"
            />
            <div className="flex items-center justify-center gap-1.5">
              <span className="font-semibold text-sm">{displayName}</span>
              {user.accountType === 'agent' && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-500 font-medium">
                  {t('user.ai')}
                </span>
              )}
              {isGuest && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 font-medium">
                  Guest
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">@{user.username}</div>
            {isGuest ? (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={goSignup}
                  className="btn-primary w-full justify-center text-sm"
                >
                  {t('userMenu.signUpToKeep')}
                </button>
                {/* 14 = guest GC inactivity TTL (GUEST_GC_DEFAULT_INACTIVITY_DAYS). */}
                <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                  {t('userMenu.guestDeletionNotice')}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-1.5 mt-1.5">
                <span className="text-xs font-medium text-foreground/80">
                  {planName || t('user.freePlan')}
                </span>
                <NavRow
                  href="/wallet"
                  openHref={openHref}
                  close={close}
                  as="link"
                  className="text-xs text-foreground font-bold hover:underline"
                >
                  {planName ? t('user.manage') : t('user.upgrade')}
                </NavRow>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <div className="p-2 border-b border-border">
            <div className="flex items-center gap-0.5 p-1 bg-muted rounded-lg">
              {themeOptions.map(({ value, icon: Icon }) => (
                <Tip key={value} label={value.charAt(0).toUpperCase() + value.slice(1)}>
                  <button
                    onClick={() => {
                      haptic('selection');
                      setTheme(value);
                    }}
                    className={cn(
                      'flex-1 flex items-center justify-center py-1.5 rounded text-xs font-medium transition-all',
                      theme === value
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </button>
                </Tip>
              ))}
            </div>
          </div>

          {/* Caller-supplied sections (e.g. Workspaces) */}
          {extraSections.map((section) =>
            section.items.length === 0 ? null : (
              <div key={section.id} className="p-1.5 border-t border-border/50">
                {section.title && (
                  <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.title}
                  </div>
                )}
                {section.items.map((item) => renderItem(item))}
              </div>
            ),
          )}

          {/* Caller-supplied custom slot — used for richer panels that
              UserMenuItem can't express (e.g. inline rename + delete). */}
          {customSlot}

          {/* Section: Account — sits BELOW the workspaces section and
              above Install app (per product). Opens the Account hub
              dialog: a tabbed shell over Wallet and Analytics. Hidden
              for guests — the economy is gated behind a real account.

              Admin sits alongside it for staff. It was briefly a tab
              INSIDE the Account hub; that put a staff surface two clicks
              deep behind a nav of personal-account concerns, and made
              AdminView render its own second-level nav inside that one.
              Showing the row is presentation, not protection: `/admin`
              and every route behind it enforce the role server-side. */}
          {!isGuest && (
            <div className="p-1.5 border-t border-border/50">
              <NavRow href="/account" openHref={openHref} close={close}>
                <UserCircle className="h-4 w-4 text-muted-foreground" />
                <span>{t('nav.account')}</span>
              </NavRow>
              {isStaff && (
                <NavRow href="/admin" openHref={openHref} close={close}>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {user.role === 'admin'
                      ? t('sidebar.adminLabel')
                      : t('sidebar.moderationLabel')}
                  </span>
                </NavRow>
              )}
            </div>
          )}

          {/* Section: Install app, Settings, Help. Docs keeps its
              real anchor (opens in a new tab); every other entry
              flows through `openHref` so the workspace can render
              it inline as a full-screen dialog. The Admin row that
              used to live here has moved into the Account hub
              (rendered conditionally as a tab inside AccountView). */}
          <div className="p-1.5 border-t border-border/50">
            {/* Install app + Settings are hidden for guests to keep their
                menu minimal — the signup CTA is the focus. */}
            {!isGuest && (
              <>
                <NavRow href="/local" openHref={openHref} close={close}>
                  <Download className="h-4 w-4 text-muted-foreground" />
                  <span>{t('nav.installApp')}</span>
                </NavRow>
                <NavRow href="/settings" openHref={openHref} close={close}>
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span>{t('user.settings')}</span>
                </NavRow>
              </>
            )}
            <a
              href={locale === 'en' ? '/docs' : `/docs/${locale}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackNavClick('user-menu-docs');
                close();
              }}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm hover:bg-accent transition-colors"
            >
              <LifeBuoy className="h-4 w-4 text-muted-foreground" />
              <span>{t('nav.help')}</span>
            </a>
          </div>

          {/* Sign out */}
          <div className="p-1.5 border-t border-border/50">
            <button
              onClick={() => {
                trackNavClick('sidebar-sign-out');
                haptic('warning');
                close();
                logout();
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground hover:bg-accent transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>{isGuest ? 'End guest session' : t('auth.logout')}</span>
            </button>
          </div>
    </>
  );

  // Inline render path: legacy sites (sidebar / settings page) keep the
  // dropdown anchored inside `menuRef` via absolute positioning so they
  // can rely on the surrounding container for layout & outside-click.
  const inlineBody = isOpen ? (
    <div
      className={cn(
        'absolute rounded-xl border border-border bg-popover shadow-xl z-50',
        direction === 'up' ? 'bottom-full left-0 mb-2' : 'top-full left-0 mt-2',
      )}
      style={{ width }}
    >
      {bodyContent}
    </div>
  ) : null;

  // Portal render path: workspace chrome lives inside `z-30` ancestors,
  // so we must escape that stacking context for menus to land above edge
  // panels. We compute the trigger rect and pin the popover to it with
  // `position: fixed`, falling back to inline rendering on first paint
  // before measurement is ready.
  const portalBody = isOpen && anchorRect ? (
    <PopoverPortal
      role="menu"
      ariaLabel={`${displayName} menu`}
      style={{
        position: 'fixed',
        top:
          direction === 'down'
            ? Math.round(anchorRect.bottom + 8)
            : undefined,
        bottom:
          direction === 'up'
            ? Math.round(window.innerHeight - anchorRect.top + 8)
            : undefined,
        // Right-align the popover when there isn't enough room to the
        // right of the trigger (so wide menus don't overflow the
        // viewport on small screens).
        left:
          anchorRect.left + width <= window.innerWidth - 8
            ? Math.round(anchorRect.left)
            : Math.max(8, Math.round(window.innerWidth - width - 8)),
        width,
        zIndex: 200,
      }}
      className="rounded-xl border border-border bg-popover shadow-xl"
    >
      {/* Re-attach outside-click guard: the menuRef can't reach into the
          portal subtree, so we provide a local container and let the
          existing handler check both menuRef and this node. */}
      <div ref={portalBodyRef}>{bodyContent}</div>
    </PopoverPortal>
  ) : null;

  return (
    <div
      className={cn('relative', className)}
      ref={(el) => {
        // Both refs share the wrapper: outside-click guard (menuRef) needs
        // to know about the trigger click, and the anchor measurement
        // hook (triggerWrapperRef) needs the same node to compute the
        // popover origin in portal mode.
        menuRef.current = el;
        triggerWrapperRef.current = el;
      }}
    >
      {trigger ? trigger({ open: isOpen, toggle, user }) : defaultTrigger}
      {portal ? portalBody : inlineBody}
    </div>
  );
}

/**
 * Internal helper: renders either a Next `<Link>` (default navigation)
 * or a button that calls `openHref` (workspace in-app full-screen
 * dialog), keeping the row markup and analytics-friendly close call in
 * lockstep across all dropdown items. Default visual style matches the
 * dropdown item rows; pass `as="link"` for an inline text link variant
 * (used by the header's Manage/Upgrade affordance).
 */
function NavRow({
  href,
  openHref,
  close,
  children,
  as = 'row',
  className,
}: {
  href: string;
  openHref?: (href: string) => void;
  close: () => void;
  children: React.ReactNode;
  as?: 'row' | 'link';
  className?: string;
}) {
  const baseCls =
    as === 'row'
      ? 'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm hover:bg-accent transition-colors'
      : '';
  const cls = cn(baseCls, className);
  if (openHref) {
    return (
      <button
        type="button"
        onClick={() => {
          close();
          openHref(href);
        }}
        className={cn(cls, as === 'row' && 'w-full text-left')}
      >
        {children}
      </button>
    );
  }
  return (
    <Link href={href} onClick={close} className={cls}>
      {children}
    </Link>
  );
}
